import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

/* ── Animations ──────────────────────────────── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
`;

/* ── Styled Components ───────────────────────── */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f8fafc;
  background-image: radial-gradient(#cbd5e1 1.5px, transparent 1.5px);
  background-size: 32px 32px;
  background-position: 0 0;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
  padding: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 56px 48px;
  max-width: 520px;
  width: 100%;
  text-align: center;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 24px -4px rgba(0, 0, 0, 0.03);
  animation: ${fadeInUp} 0.5s ease-out;

  @media (max-width: 480px) {
    padding: 40px 24px;
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 32px;
  animation: ${float} 4s ease-in-out infinite;

  svg {
    width: 36px;
    height: 36px;
  }
`;

const BigNumber = styled.div`
  font-size: 72px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.04em;
  color: #101828;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #101828;
  margin: 0 0 12px;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  font-size: 15px;
  color: #64748b;
  line-height: 1.65;
  margin: 0 0 40px;
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: #C86A4A;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(200, 106, 74, 0.2);

  &:hover {
    background: #B65B3C;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(200, 106, 74, 0.3);
  }
`;

const SecondaryBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  background: white;
  border: 1px solid #cbd5e1;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    color: #101828;
    transform: translateY(-1px);
  }
`;

/* ── Component ───────────────────────────────── */
const ServerError = ({ onRetry }) => {
    const navigate = useNavigate();

    useEffect(() => {
        const prev = document.title;
        document.title = '500 — Server Error | Sayify';
        return () => { document.title = prev; };
    }, []);

    const handleRefresh = () => {
        if (onRetry) {
            onRetry();
        } else {
            window.location.reload();
        }
    };

    return (
        <Wrapper>
            <Card>
                <IconWrapper>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                </IconWrapper>

                <BigNumber>500</BigNumber>
                <Title>Internal Server Error</Title>
                <Description>
                    We've encountered an unexpected issue on our servers. Our team has been notified.
                </Description>

                <ButtonRow>
                    <PrimaryBtn onClick={handleRefresh}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10" />
                            <polyline points="1 20 1 14 7 14" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                        </svg>
                        Refresh Page
                    </PrimaryBtn>
                    <SecondaryBtn to="/">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                        Dashboard
                    </SecondaryBtn>
                </ButtonRow>
            </Card>
        </Wrapper>
    );
};

export default ServerError;
